import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Home, TrendingUp, ArrowRight, RefreshCw } from "lucide-react";
import { Reveal } from "./Reveal";
import { trackLead } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Input = "w-full bg-white text-[#0a0a0a] border border-black/20 px-4 py-3 text-base focus:outline-none focus:ring-1 focus:ring-[#0a0a0a] focus:border-[#0a0a0a] transition-colors";

const PPSF = { "Redlands": 310, "Palm Springs": 470, "Temecula": 300, "Other SoCal area": 350 };
const CONDITION_FACTOR = { "Move-in ready": 1.06, "Lightly updated": 1.0, "Needs some work": 0.9 };

const fmt = (n) => "$" + (Math.round(n / 1000) * 1000).toLocaleString();

export const HomeValuation = () => {
  const [form, setForm] = useState({
    address: "", area: "", beds: "", baths: "", sqft: "",
    condition: "", timeframe: "", name: "", email: "", phone: ""
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const sqft = parseInt(form.sqft, 10);
    if (!form.address.trim()) return toast.error("Please enter your property address");
    if (!form.area) return toast.error("Please select your area");
    if (!sqft || sqft < 200) return toast.error("Please enter a valid square footage");
    if (!form.condition) return toast.error("Please select the condition");
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.email.trim() && !form.phone.trim()) return toast.error("Add an email or phone so we can send your report");

    const ppsf = PPSF[form.area] || 350;
    const factor = CONDITION_FACTOR[form.condition] || 1.0;
    const base = sqft * ppsf * factor;
    const low = base * 0.93;
    const high = base * 1.07;

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        source: "home_valuation",
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        area: form.area,
        message: `Home valuation request: ${form.address}`,
        quiz_answers: {
          address: form.address, area: form.area, beds: form.beds, baths: form.baths,
          sqft: String(sqft), condition: form.condition, timeframe: form.timeframe || "n/a",
          estimate: `${fmt(low)} – ${fmt(high)}`
        }
      });
      trackLead({ source: "home_valuation", area: form.area });
      setResult({ low, high, mid: base });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setForm({ address: "", area: "", beds: "", baths: "", sqft: "", condition: "", timeframe: "", name: "", email: "", phone: "" });
  };

  return (
    <section id="home-value" data-testid="home-value-section" className="py-24 md:py-32 bg-[#0a0a0a] text-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <Reveal className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <Home className="text-gold" size={22} />
            <p className="eyebrow text-gold">Home Evaluation</p>
          </div>
          <h2 className="font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight">
            What's my home worth?
          </h2>
          <p className="text-white/70 font-light text-lg mt-6 leading-relaxed">
            Get an instant estimated value range for your Southern California home — then Melissa
            will follow up with a free, detailed Comparative Market Analysis tailored to your exact property.
          </p>
          <ul className="mt-8 space-y-3 text-white/70 font-light text-sm">
            <li className="flex items-center gap-3"><TrendingUp size={16} className="text-gold" /> Instant estimated range</li>
            <li className="flex items-center gap-3"><TrendingUp size={16} className="text-gold" /> Free professional CMA follow-up</li>
            <li className="flex items-center gap-3"><TrendingUp size={16} className="text-gold" /> No obligation, ever</li>
          </ul>
        </Reveal>

        <div className="lg:col-span-7">
          {result ? (
            <Reveal>
              <div className="bg-white text-[#0a0a0a] p-8 sm:p-12" data-testid="home-value-result">
                <p className="eyebrow text-[11px] text-[#a1a1aa]">Estimated value range</p>
                <p className="font-heading text-4xl sm:text-5xl text-gold mt-3" data-testid="home-value-range">
                  {fmt(result.low)} – {fmt(result.high)}
                </p>
                <p className="text-[#52525b] font-light mt-5 leading-relaxed">
                  Thanks, <span className="text-[#0a0a0a] font-medium">{form.name}</span>! This is an
                  automated estimate for <span className="text-[#0a0a0a] font-medium">{form.address}</span>.
                  Melissa will personally prepare and send you a precise Comparative Market Analysis
                  using real, recent sales near your home.
                </p>
                <p className="text-xs text-[#a1a1aa] font-light mt-5">
                  Estimate based on area price-per-square-foot and condition. Not an appraisal — actual value depends on upgrades, lot, and market conditions.
                </p>
                <button onClick={reset} data-testid="home-value-reset" className="inline-flex items-center gap-2 mt-8 border border-black/20 px-6 py-3 text-sm hover:bg-[#0a0a0a] hover:text-white transition-colors">
                  <RefreshCw size={15} /> Evaluate another home
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="bg-white/5 border border-white/10 p-8 sm:p-10 space-y-4" data-testid="home-value-form">
                <input data-testid="hv-address" className={Input} placeholder="Property address" value={form.address} onChange={(e) => set("address", e.target.value)} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <select data-testid="hv-area" className={Input} value={form.area} onChange={(e) => set("area", e.target.value)}>
                    <option value="">Area</option>
                    <option>Redlands</option>
                    <option>Palm Springs</option>
                    <option>Temecula</option>
                    <option>Other SoCal area</option>
                  </select>
                  <select data-testid="hv-condition" className={Input} value={form.condition} onChange={(e) => set("condition", e.target.value)}>
                    <option value="">Condition</option>
                    <option>Move-in ready</option>
                    <option>Lightly updated</option>
                    <option>Needs some work</option>
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <input data-testid="hv-beds" type="number" min="0" className={Input} placeholder="Beds" value={form.beds} onChange={(e) => set("beds", e.target.value)} />
                  <input data-testid="hv-baths" type="number" min="0" step="0.5" className={Input} placeholder="Baths" value={form.baths} onChange={(e) => set("baths", e.target.value)} />
                  <input data-testid="hv-sqft" type="number" min="0" className={Input} placeholder="Sq ft" value={form.sqft} onChange={(e) => set("sqft", e.target.value)} />
                </div>
                <div className="h-px bg-white/10 my-2" />
                <input data-testid="hv-name" className={Input} placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="hv-email" type="email" className={Input} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  <input data-testid="hv-phone" type="tel" className={Input} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <input data-testid="hv-timeframe" className={Input} placeholder="When are you thinking of selling? (optional)" value={form.timeframe} onChange={(e) => set("timeframe", e.target.value)} />
                <button data-testid="hv-submit" type="submit" disabled={loading} className="flex items-center justify-center gap-2 w-full bg-gold text-white py-4 text-sm tracking-wide hover:bg-gold-hover transition-colors disabled:opacity-60">
                  {loading ? "Calculating..." : "Get My Home Value"} <ArrowRight size={16} />
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
